/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  MaintenanceScheduleProperties,
  MaintenanceScheduleRequest,
  MaintenanceScheduleResponse,
  MaintenanceScheduleInitialProperties,
} from '../../interface/maintenance-schedule.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import device from '../../utils/api/device';

// a thunk to fetch users
export const fetchMaintenanceSchedule = createAsyncThunk<
  MaintenanceScheduleResponse,
  MaintenanceScheduleRequest
>(
  'maintenance-schedule/fetchMaintenanceSchedule',
  async (data: MaintenanceScheduleRequest, { dispatch }) => {
    try {
      const response = await device.fetchMaintenanceSchedules(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// a thunk to refresh maintenance schedule
export const refreshMaintenanceSchedule = createAsyncThunk<
  MaintenanceScheduleResponse,
  MaintenanceScheduleRequest
>(
  'maintenance-schedule/refreshMaintenanceSchedule',
  async (data: MaintenanceScheduleRequest) => {
    const response = await device.fetchMaintenanceSchedules(data);
    return response.data;
  },
);


// a thunk to fetch devices filters
export const fetchMaintenanceScheduleFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('device-transaction/fetchMaintenanceScheduleFilters', async (_, { dispatch }) => {
  try {
    const response = await device.fetchMaintenanceScheduleFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: MaintenanceScheduleInitialProperties = {
  schedules: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: deviceFilter,
  filterListLoading: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const MaintenanceScheduleSlice = createSlice({
  name: 'maintenance-schedule',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateMaintenanceScheduleFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter } = UpdateCommonFilter(state.filters, action.payload);
        state.filters = updatedCommonFilter;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchMaintenanceSchedule.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMaintenanceSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.schedules = action.payload?.results?.length
          ? action.payload.results?.map((scheduleData: MaintenanceScheduleProperties) => ({
              ...scheduleData,
              id: scheduleData._id,
              date: ConvertUTCtoDate(scheduleData?.startDateUtc),
              time: FormatUtcString(ConvertUTCtoHHMM(scheduleData?.startDateUtc)),
              name: scheduleData?.chargingStationName,
              address: scheduleData?.chargingStationAddress,
              duration: scheduleData?.duration || '-',
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchMaintenanceSchedule.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(refreshMaintenanceSchedule.fulfilled, (state, action) => {
      state.schedules = action.payload?.results?.length
        ? action.payload.results?.map((scheduleData: MaintenanceScheduleProperties) => ({
            ...scheduleData,
            id: scheduleData._id,
            date: ConvertUTCtoDate(scheduleData?.startDateUtc),
            time: FormatUtcString(ConvertUTCtoHHMM(scheduleData?.startDateUtc)),
            name: scheduleData?.chargingStationName,
            address: scheduleData?.chargingStationAddress,
            duration: scheduleData?.duration || '-',
          }))
        : [];
      state.limit = action.payload.limit;
      state.page = action.payload.page;
      state.totalPages = action.payload.totalPages;
      state.totalResults = action.payload.totalResults;
    });
    builder
      .addCase(fetchMaintenanceScheduleFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchMaintenanceScheduleFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchMaintenanceScheduleFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateMaintenanceScheduleFilters, setSortData } = MaintenanceScheduleSlice.actions;
// Export the reducer
export default MaintenanceScheduleSlice.reducer;
